import React from 'react';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Navigation Component with Enhanced Accessibility
 * 
 * Provides universal "Retour" and "Suivant/Continuer" buttons
 * Compatible with keyboard/tabulation navigation and screen readers
 * for users with reduced mobility or visual impairment
 */

// Universal Back Button with Enhanced Accessibility
export const BackButton = ({ 
  onClick, 
  label = "Retour", 
  disabled = false, 
  className = "",
  variant = "default" // "default", "minimal", "floating"
}) => {
  const baseClasses = "flex items-center gap-2 font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2";
  
  const variantClasses = {
    default: "bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg border border-gray-300",
    minimal: "text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded",
    floating: "bg-white shadow-md hover:shadow-lg text-gray-700 px-4 py-2 rounded-full border"
  };

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e) => {
    // Enhanced keyboard navigation
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      // Enhanced accessibility attributes
      aria-label={`${label} - Revenir à la page précédente`}
      aria-describedby="back-button-description"
      role="button"
      tabIndex={disabled ? -1 : 0}
    >
      <ArrowLeft 
        className="h-4 w-4" 
        aria-hidden="true"
      />
      <span>{label}</span>
      
      {/* Hidden description for screen readers */}
      <span 
        id="back-button-description" 
        className="sr-only"
      >
        Appuyez sur Entrée ou Espace pour revenir à la page précédente. Navigation sans perte de données.
      </span>
    </button>
  );
};

// Universal Next/Continue Button with Enhanced Accessibility
export const NextButton = ({ 
  onClick, 
  label = "Suivant", 
  disabled = false, 
  className = "",
  variant = "primary", // "primary", "secondary", "success"
  isLoading = false
}) => {
  const baseClasses = "flex items-center gap-2 font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2";
  
  const variantClasses = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg focus:ring-blue-500",
    secondary: "bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg focus:ring-gray-500", 
    success: "bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg focus:ring-green-500"
  };

  const handleClick = () => {
    if (!disabled && !isLoading && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled || isLoading}
      className={`${baseClasses} ${variantClasses[variant]} ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      // Enhanced accessibility attributes
      aria-label={`${label} - ${isLoading ? 'Chargement en cours' : 'Continuer vers la prochaine étape'}`}
      aria-describedby="next-button-description"
      aria-busy={isLoading}
      role="button"
      tabIndex={disabled || isLoading ? -1 : 0}
    >
      <span>{label}</span>
      {isLoading ? (
        <div 
          className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" 
          aria-hidden="true"
        />
      ) : (
        <ArrowRight 
          className="h-4 w-4" 
          aria-hidden="true"
        />
      )}
      
      {/* Hidden description for screen readers */}
      <span 
        id="next-button-description" 
        className="sr-only"
      >
        {isLoading 
          ? "Opération en cours, veuillez patienter"
          : "Appuyez sur Entrée ou Espace pour continuer vers l'étape suivante. Vos données seront conservées."
        }
      </span>
    </button>
  );
};

// Navigation Bar Component for Multi-step Forms
export const NavigationBar = ({ 
  onBack, 
  onNext, 
  backLabel = "Retour",
  nextLabel = "Suivant",
  canGoBack = true,
  canGoNext = true,
  isLoading = false,
  className = "",
  position = "bottom" // "top", "bottom", "floating"
}) => {
  const positionClasses = {
    top: "sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm",
    bottom: "sticky bottom-0 z-40 bg-white border-t border-gray-200 shadow-sm",
    floating: "fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-white rounded-lg shadow-lg border border-gray-200"
  };

  return (
    <nav 
      className={`${positionClasses[position]} px-4 py-3 ${className}`}
      role="navigation"
      aria-label="Navigation du formulaire en plusieurs étapes"
    >
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        {/* Back Button */}
        <div>
          {canGoBack && onBack && (
            <BackButton 
              onClick={onBack}
              label={backLabel}
              variant="minimal"
            />
          )}
        </div>

        {/* Next/Continue Button */}
        <div>
          {canGoNext && onNext && (
            <NextButton 
              onClick={onNext}
              label={nextLabel}
              isLoading={isLoading}
              variant="primary"
            />
          )}
        </div>
      </div>
      
      {/* Accessibility landmark for screen readers */}
      <div 
        className="sr-only" 
        role="status" 
        aria-live="polite"
      >
        Navigation disponible: {canGoBack ? 'Retour possible' : 'Première étape'}
        {canGoNext ? ', Suivant disponible' : ', Dernière étape'}
      </div>
    </nav>
  );
};

// Breadcrumb Navigation for Complex Flows
export const BreadcrumbNavigation = ({ 
  steps = [], 
  currentStep = 0, 
  onStepClick = null,
  className = "" 
}) => {
  return (
    <nav 
      className={`mb-6 ${className}`}
      role="navigation"
      aria-label="Fil d'Ariane - Progression dans les étapes"
    >
      <ol className="flex items-center space-x-2 text-sm">
        {steps.map((step, index) => {
          const isCurrent = index === currentStep;
          const isCompleted = index < currentStep;
          const isClickable = onStepClick && (isCompleted || index <= currentStep);
          
          return (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <ChevronRight 
                  className="h-4 w-4 text-gray-400 mx-1" 
                  aria-hidden="true"
                />
              )}
              
              {isClickable ? (
                <button
                  onClick={() => onStepClick(index)}
                  className={`px-3 py-1 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                    isCurrent 
                      ? 'bg-blue-100 text-blue-800 font-medium' 
                      : isCompleted
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  aria-current={isCurrent ? 'step' : undefined}
                  aria-label={`${step}${isCurrent ? ' - Étape actuelle' : isCompleted ? ' - Étape complétée' : ''}`}
                  tabIndex={0}
                >
                  {step}
                </button>
              ) : (
                <span 
                  className={`px-3 py-1 rounded-md ${
                    isCurrent 
                      ? 'bg-blue-100 text-blue-800 font-medium' 
                      : isCompleted
                        ? 'text-green-600'
                        : 'text-gray-400'
                  }`}
                  aria-current={isCurrent ? 'step' : undefined}
                  aria-label={`${step}${isCurrent ? ' - Étape actuelle' : isCompleted ? ' - Étape complétée' : ''}`}
                >
                  {step}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

// Keyboard Navigation Helper Hook
export const useKeyboardNavigation = (onBack, onNext) => {
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Alt + Left Arrow for Back
      if (e.altKey && e.key === 'ArrowLeft' && onBack) {
        e.preventDefault();
        onBack();
      }
      
      // Alt + Right Arrow for Next  
      if (e.altKey && e.key === 'ArrowRight' && onNext) {
        e.preventDefault();
        onNext();
      }
      
      // Escape to go back
      if (e.key === 'Escape' && onBack) {
        onBack();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onBack, onNext]);
};

export default {
  BackButton,
  NextButton,
  NavigationBar,
  BreadcrumbNavigation,
  useKeyboardNavigation
};